import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { 
  FaUsers, FaUserTie, FaHandshake, FaChartLine, FaCalendarCheck,
  FaCheckCircle, FaArrowRight, FaBars, FaTimes,
  FaDollarSign, FaShieldAlt, FaNetworkWired, FaHeart
} from 'react-icons/fa';
import { BrandLogo } from '../components/BrandLogo';

function BecomePartner() {
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const benefits = [
    {
      icon: FaNetworkWired,
      title: "Access Family Networks",
      description: "Connect with families actively seeking professional guidance for wealth building, estate planning, and financial empowerment."
    },
    {
      icon: FaCalendarCheck,
      title: "Integrated Scheduling",
      description: "Families can book consultations directly through the platform. Manage your availability and appointments seamlessly."
    },
    {
      icon: FaChartLine,
      title: "Grow Your Practice",
      description: "Reach new clients who value family legacy and long-term financial planning. Build lasting relationships across generations."
    },
    {
      icon: FaShieldAlt,
      title: "Trusted Platform",
      description: "Be part of a vetted network of professionals. Families trust our directory for quality advisors committed to their success."
    },
    {
      icon: FaHeart,
      title: "Mission-Aligned",
      description: "Work with families focused on building generational wealth, preserving heritage, and empowering future generations."
    },
    {
      icon: FaDollarSign,
      title: "Flexible Pricing",
      description: "Set your own rates. Offer free initial consultations or premium services—you're in control of your business."
    }
  ];

  const professionalTypes = [
    { icon: '💼', title: 'Financial Advisors', desc: 'Help families build and preserve wealth across generations' },
    { icon: '⚖️', title: 'Estate Attorneys', desc: 'Guide families through estate planning and legacy protection' },
    { icon: '📊', title: 'CPAs & Tax Experts', desc: 'Optimize family finances and tax strategies' },
    { icon: '🏠', title: 'Real Estate Experts', desc: 'Assist with property investments and family real estate' },
    { icon: '🛡️', title: 'Insurance Specialists', desc: 'Protect families with comprehensive coverage solutions' },
    { icon: '❤️', title: 'Family Therapists', desc: 'Support family dynamics, communication, and wellness' }
  ];


  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50">
      {/* Header */}
      <header className="bg-white shadow-sm">
        <div className="container mx-auto px-4 py-4 flex justify-between items-center">
          <Link to="/" className="flex items-center space-x-2">
            <BrandLogo className="w-9 h-9 sm:w-11 sm:h-11" testId="partner-header-logo" />
            <h1 className="text-xl sm:text-2xl font-bold text-gray-800">Legacy Forge Family</h1>
          </Link>
          
          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-4">
            <button
              onClick={() => navigate('/professionals/directory')}
              className="px-4 py-2 text-gray-600 hover:text-gray-900 font-medium"
            >
              View Directory
            </button>
            <button
              onClick={() => navigate('/login')}
              className="px-4 py-2 text-gray-600 hover:text-gray-900 font-medium"
            >
              Log In
            </button>
            <button
              onClick={() => navigate('/professionals/signup')}
              className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium"
            >
              Apply Now
            </button>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="md:hidden p-2 text-gray-600 hover:text-gray-900"
          >
            {mobileMenuOpen ? <FaTimes className="text-xl" /> : <FaBars className="text-xl" />}
          </button>
        </div>

        {/* Mobile Navigation Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden absolute top-full left-0 right-0 bg-white shadow-lg border-t z-50">
            <div className="container mx-auto px-4 py-4 flex flex-col space-y-3">
              <button
                onClick={() => { navigate('/professionals/directory'); setMobileMenuOpen(false); }}
                className="w-full text-left px-4 py-3 text-gray-600 hover:bg-gray-50 rounded-lg font-medium"
              >
                View Directory
              </button>
              <button
                onClick={() => { navigate('/login'); setMobileMenuOpen(false); }}
                className="w-full text-left px-4 py-3 text-gray-600 hover:bg-gray-50 rounded-lg font-medium"
              >
                Log In
              </button>
              <button
                onClick={() => { navigate('/professionals/signup'); setMobileMenuOpen(false); }}
                className="w-full px-4 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium text-center"
              >
                Apply Now
              </button>
            </div>
          </div>
        )}
      </header>

      {/* Hero Section */}
      <section className="container mx-auto px-4 py-12 sm:py-20">
        <div className="max-w-4xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 bg-blue-100 text-blue-800 px-4 py-2 rounded-full text-sm font-medium mb-6">
            <FaHandshake />
            <span>Professional Partnership Program</span>
          </div>
          
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
            Help Families Build <span className="text-blue-600">Generational Wealth</span>
          </h1>
          
          <p className="text-base sm:text-lg lg:text-xl text-gray-600 mb-8 max-w-3xl mx-auto">
            Join Legacy Forge's network of trusted professionals. Connect with families committed to 
            financial empowerment, estate planning, and building legacies that last for generations.
          </p>
          
          <div className="flex flex-col sm:flex-row justify-center gap-4">
            <button
              onClick={() => navigate('/professionals/signup')}
              className="px-8 py-4 bg-blue-600 text-white text-lg rounded-lg hover:bg-blue-700 font-semibold shadow-lg transform hover:scale-105 transition flex items-center justify-center gap-2"
            >
              <FaUserTie />
              Apply to Join
            </button>
            <button
              onClick={() => navigate('/professionals/directory')}
              className="px-8 py-4 bg-white text-blue-600 text-lg rounded-lg hover:bg-gray-50 font-semibold shadow-lg border-2 border-blue-600 transform hover:scale-105 transition"
            >
              View Current Partners
            </button>
          </div>
        </div>
      </section>

      {/* Professional Types */}
      <section className="bg-white py-16">
        <div className="container mx-auto px-4">
          <h2 className="text-2xl sm:text-3xl font-bold text-center text-gray-900 mb-4">
            Who We're Looking For
          </h2>
          <p className="text-center text-gray-600 mb-10 max-w-2xl mx-auto">
            We partner with professionals who share our commitment to helping families thrive
          </p>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 max-w-5xl mx-auto">
            {professionalTypes.map((type, idx) => (
              <div 
                key={idx}
                className="bg-gradient-to-br from-slate-50 to-blue-50 p-6 rounded-xl border border-gray-200 hover:shadow-lg transition"
              >
                <div className="text-4xl mb-3">{type.icon}</div>
                <h3 className="font-bold text-gray-900 mb-2">{type.title}</h3>
                <p className="text-gray-600 text-sm">{type.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <h2 className="text-2xl sm:text-3xl font-bold text-center text-gray-900 mb-4">
            Why Partner With Us
          </h2>
          <p className="text-center text-gray-600 mb-10 max-w-2xl mx-auto">
            Legacy Forge provides the tools and connections you need to grow your practice
          </p>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {benefits.map((benefit, idx) => (
              <div key={idx} className="bg-white p-6 rounded-xl shadow-md hover:shadow-lg transition">
                <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mb-4">
                  <benefit.icon className="text-blue-600 text-xl" />
                </div>
                <h3 className="font-bold text-gray-900 mb-2">{benefit.title}</h3>
                <p className="text-gray-600 text-sm">{benefit.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="bg-blue-600 py-16 text-white">
        <div className="container mx-auto px-4">
          <h2 className="text-2xl sm:text-3xl font-bold text-center mb-10">
            How It Works
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8 max-w-5xl mx-auto">
            {[
              { step: 1, title: "Apply", desc: "Submit your application with credentials and experience" },
              { step: 2, title: "Get Verified", desc: "Our team reviews your profile and qualifications" },
              { step: 3, title: "Create Profile", desc: "Build your professional profile showcasing your expertise" },
              { step: 4, title: "Connect", desc: "Families discover you and book consultations" }
            ].map((item) => (
              <div key={item.step} className="text-center">
                <div className="w-12 h-12 bg-white text-blue-600 rounded-full flex items-center justify-center font-bold text-xl mx-auto mb-4">
                  {item.step}
                </div>
                <h3 className="font-bold mb-2">{item.title}</h3>
                <p className="text-blue-100 text-sm">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-gradient-to-r from-blue-600 to-indigo-700">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-2xl sm:text-3xl font-bold text-white mb-4">
            Ready to Make a Difference?
          </h2>
          <p className="text-blue-100 mb-8 max-w-2xl mx-auto">
            Join our growing network of professionals helping families build lasting legacies. 
            Applications are reviewed within 48 hours.
          </p>
          <button
            onClick={() => navigate('/professionals/signup')}
            className="px-8 py-4 bg-white text-blue-600 text-lg rounded-lg hover:bg-gray-100 font-semibold shadow-lg transform hover:scale-105 transition inline-flex items-center gap-2"
          >
            Apply Now
            <FaArrowRight />
          </button>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <h2 className="text-2xl sm:text-3xl font-bold text-center text-gray-900 mb-10">
            Frequently Asked Questions
          </h2>
          
          <div className="max-w-3xl mx-auto space-y-6">
            {[
              {
                q: "Is there a fee to join the partner network?",
                a: "Currently, joining our professional network is free. We may introduce premium features in the future, but basic listing and consultation booking will remain accessible."
              },
              {
                q: "How are professionals vetted?",
                a: "We review credentials, experience, and professional background. We verify licenses where applicable and may request references to ensure quality for our family users."
              },
              {
                q: "How do consultations work?",
                a: "Families can view your profile, see your availability, and request consultations. You control your schedule and can offer free initial consultations or set your own rates."
              },
              {
                q: "What kind of families use Legacy Forge?",
                a: "Our families are focused on building generational wealth, preserving heritage, and strengthening family bonds. They're typically planning reunions, managing family finances, or working on estate planning."
              },
              {
                q: "Can I manage my own availability?",
                a: "Yes! You have full control over your calendar, consultation types, and availability. The platform integrates with your schedule so you never miss a booking."
              }
            ].map((faq, idx) => (
              <div key={idx} className="bg-white p-6 rounded-xl shadow-md">
                <h3 className="font-bold text-gray-900 mb-2">{faq.q}</h3>
                <p className="text-gray-600">{faq.a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-800 text-white py-8">
        <div className="container mx-auto px-4">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6">
            <div className="text-center md:text-left">
              <div className="flex items-center justify-center md:justify-start space-x-2 mb-2">
                <BrandLogo className="w-9 h-9" testId="partner-footer-logo" />
                <h3 className="text-xl font-bold">Legacy Forge Family</h3>
              </div>
              <p className="text-gray-300 text-sm">Professional Partnership Program</p>
            </div>
            
            <div className="flex flex-wrap justify-center gap-4 sm:gap-6 text-sm">
              <button
                onClick={() => navigate('/')}
                className="text-gray-300 hover:text-white transition"
              >
                Home
              </button>
              <button
                onClick={() => navigate('/professionals/directory')}
                className="text-gray-300 hover:text-white transition"
              >
                Directory
              </button>
              <button
                onClick={() => navigate('/terms')}
                className="text-gray-300 hover:text-white transition"
              >
                Terms of Service
              </button>
              <button
                onClick={() => navigate('/privacy')}
                className="text-gray-300 hover:text-white transition"
              >
                Privacy Policy
              </button>
            </div>
          </div>
          
          <div className="mt-6 pt-6 border-t border-gray-700 text-center">
            <p className="text-gray-500 text-xs">&copy; 2025 Legacy Forge Family. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
}

export default BecomePartner;
